import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://isco-backend-deployment-932661235642.us-central1.run.app';

/**
 * Batch process multiple clusters - generate and save itineraries in parallel
 * Recommended for most use cases (up to 100 clusters)
 * @param {Array} clusters - Array of cluster objects from cluster-stops endpoint
 * @param {string} startDate - Optional start date (YYYY-MM-DD format)
 * @param {string} endDate - Optional end date (YYYY-MM-DD format)
 * @param {Object} filterParams - Original filter parameters used
 * @param {string} token - JWT authentication token
 * @returns {Promise<Object>} - Result object with successful/failed arrays
 */
export const batchGenerateAndSaveItineraries = async (clusters, startDate, endDate, filterParams, token) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/v2/batch/generate-and-save-itineraries`,
    {
      clusters,
      start_date: startDate,
      end_date: endDate,
      filter_params: filterParams
    },
    {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }
  );
  return response.data;
};

/**
 * Ultra high-throughput batch processing for large cluster sets
 * Use this for 100+ clusters for maximum performance
 * @param {Array} clusters - Array of cluster objects
 * @param {string} startDate - Optional start date
 * @param {string} endDate - Optional end date
 * @param {Object} filterParams - Original filter parameters
 * @param {string} token - JWT authentication token
 * @returns {Promise<Object>} - Result object with processing metrics
 */
export const ultraHighThroughputBatch = async (clusters, startDate, endDate, filterParams, token) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/v2/batch/ultra-high-throughput`,
    {
      clusters,
      start_date: startDate,
      end_date: endDate,
      filter_params: filterParams
    },
    {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }
  );
  return response.data;
};

/**
 * Complete parallel clustering pipeline - filter, cluster, generate, and save in one call
 * Simplest option - handles entire flow in one API call
 * @param {Object} filters - Filter criteria
 * @param {Object} clusteringParams - Clustering parameters
 * @param {string} token - JWT authentication token
 * @returns {Promise<Array>} - Array of saved cluster objects
 */
export const parallelClusterPipeline = async (filters, clusteringParams, token) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/v2/batch/parallel-cluster`,
    {
      filters,
      clustering_params: clusteringParams
    },
    {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }
  );
  return response.data;
};

/**
 * Retry failed clusters from a batch operation
 * @param {Array} failedClusters - Array of failed cluster objects
 * @param {string} token - JWT authentication token
 * @returns {Promise<Object>} - Result object with successful/failed arrays
 */
export const retryFailedClusters = async (failedClusters, token) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/v2/batch/retry-failed`,
    {
      clusters: failedClusters
    },
    {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }
  );
  return response.data;
};

/**
 * Smart batch processor - automatically chooses best endpoint based on cluster count
 * @param {Array} clusters - Array of cluster objects
 * @param {string} startDate - Optional start date
 * @param {string} endDate - Optional end date
 * @param {Object} filterParams - Original filter parameters
 * @param {string} token - JWT authentication token
 * @param {Function} onProgress - Progress callback function
 * @returns {Promise<Object>} - Result object with successful/failed arrays
 */
export const smartBatchProcess = async (clusters, startDate, endDate, filterParams, token, onProgress) => {
  const clusterCount = clusters.length;
  
  if (onProgress) {
    onProgress({
      step: 'batch',
      message: `Processing ${clusterCount} clusters in parallel...`
    });
  }
  
  let result;
  
  try {
    if (clusterCount <= 10) {
      // Small batch - use standard endpoint
      result = await batchGenerateAndSaveItineraries(
        clusters, 
        startDate, 
        endDate, 
        filterParams, 
        token
      );
    } else if (clusterCount <= 100) {
      // Medium batch - use standard endpoint with progress updates
      result = await batchGenerateAndSaveItineraries(
        clusters, 
        startDate, 
        endDate, 
        filterParams, 
        token
      );
    } else {
      // Large batch - use ultra high-throughput endpoint
      if (onProgress) {
        onProgress({
          step: 'batch',
          message: `Processing ${clusterCount} clusters...`
        });
      }
      result = await ultraHighThroughputBatch(
        clusters, 
        startDate, 
        endDate, 
        filterParams, 
        token
      );
    }
    
    if (onProgress && result) {
      const successCount = result.successful?.length || 0;
      const failedCount = result.failed?.length || 0;
      const processingTime = result.processing_time || 0;
      
      onProgress({
        step: 'complete',
        message: `Completed! ${successCount} successful, ${failedCount} failed in ${processingTime.toFixed(1)}s`
      });
    }
    
    return result;
    
  } catch (error) {
    console.error('Batch processing failed:', error);
    throw error;
  }
};

/**
 * Complete clustering flow with batch processing - replaces old sequential approach
 * This is the main function to use for the complete flow with batch optimization
 * @param {Object} filters - Filter criteria
 * @param {Object} clusteringParams - Clustering parameters
 * @param {string} token - JWT authentication token
 * @param {Function} onProgress - Progress callback function
 * @returns {Promise<Array>} - Array of saved cluster objects
 */
export const createClustersWithBatchProcessing = async (filters, clusteringParams, token, onProgress) => {
  try {
    // For complete pipeline, use the parallel-cluster endpoint
    if (onProgress) {
      onProgress({
        step: 'pipeline',
        message: 'Starting parallel clustering pipeline...'
      });
    }
    
    const result = await parallelClusterPipeline(filters, clusteringParams, token);
    
    // The parallel-cluster endpoint returns an array of saved clusters directly
    if (Array.isArray(result)) {
      if (onProgress) {
        onProgress({
          step: 'complete',
          message: `Generated ${result.length} schedules successfully!`
        });
      }
      return result;
    }
    
    // If result has successful/failed structure
    if (result.successful) {
      if (onProgress) {
        const successCount = result.successful.length;
        const failedCount = result.failed?.length || 0;
        const processingTime = result.processing_time || 0;
        
        onProgress({
          step: 'complete',
          message: `Completed! ${successCount} successful, ${failedCount} failed in ${processingTime.toFixed(1)}s`
        });
      }
      
      // Log any failures for debugging
      if (result.failed?.length > 0) {
        console.warn('Some clusters failed:', result.failed);
      }
      
      return result.successful;
    }
    
    return [];
    
  } catch (error) {
    console.error('Batch clustering failed:', error);
    throw error;
  }
};

/**
 * Batch processing with retry logic for different min_samples values
 * Uses the complete pipeline with automatic retry on failure
 * @param {Object} filters - Filter criteria
 * @param {Object} clusteringParams - Initial clustering parameters
 * @param {string} token - JWT authentication token
 * @param {Function} onProgress - Progress callback function
 * @returns {Promise<Array>} - Array of saved cluster objects
 */
export const createClustersWithBatchRetry = async (filters, clusteringParams, token, onProgress) => {
  const originalMinSamples = clusteringParams.min_samples || 12;
  const fallbackValues = [8, 5, 3, 2, 1];
  
  // Try with original min_samples first
  try {
    console.log(`Attempting batch clustering with min_samples: ${originalMinSamples}`);
    const result = await createClustersWithBatchProcessing(filters, clusteringParams, token, onProgress);
    
    if (result.length > 0) {
      return result;
    }
  } catch (error) {
    console.error(`Batch clustering failed with min_samples ${originalMinSamples}:`, error);
  }
  
  // Try with progressively smaller min_samples values
  for (const fallbackMinSamples of fallbackValues) {
    if (fallbackMinSamples >= originalMinSamples) continue;
    
    try {
      console.log(`Retrying batch clustering with reduced min_samples: ${fallbackMinSamples}`);
      const fallbackParams = { ...clusteringParams, min_samples: fallbackMinSamples };
      const result = await createClustersWithBatchProcessing(filters, fallbackParams, token, onProgress);
      
      if (result.length > 0) {
        console.log(`Successfully generated schedules with min_samples: ${fallbackMinSamples}`);
        return result;
      }
    } catch (error) {
      console.error(`Batch clustering failed with min_samples ${fallbackMinSamples}:`, error);
    }
  }
  
  throw new Error('Failed to generate clusters with all attempted min_samples values');
};